import { blobToUrl } from '@Utils/helpers/imageHelpers';
import {
    convertMetersToFeet,
    convertRadiansToDegrees,
    roundToFourSigFigs,
} from '@Utils/helpers/numericalHelpers';
import { SortOrder } from '@Utils/helpers/sortingHelpers';
import { splitCamelCase } from '@Utils/helpers/stringHelpers';
import { Geometry3D, PitchGeometryFactory, Vector } from '@ikegps/core-shared';
import { GeometryCalibration } from '@ikegps/core-shared/build/src/math/geometry/geometry.3d';
import axios, { CancelToken, CancelTokenSource } from 'axios';
import _cloneDeep from 'lodash/cloneDeep';
import _groupBy from 'lodash/groupBy';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import _orderBy from 'lodash/orderBy';
import { entityEndPoints } from '@Services/data/ApiHandler';
import { GlobalKeys } from '@Services/globalVariables/GlobalKeys';
import {
    addGlobalVariable,
    clearGlobalVariable,
    getGlobalVariable,
    hasGlobalValue,
} from '@Services/globalVariables/GlobalVariableManager';
import { error } from '@Services/notification/NotificationServices';
import store from '@Services/redux/Store';
import {
    Anchor,
    AttachmentPoint,
    Equipment,
    FormGenericDatumValue,
    GetSpansPole,
    Guy,
    IAnalysisEntity,
    IQcPole,
    IQcSpanEntity,
    IReworkReason,
    ISelectedPoleAttachedSpan,
    ISvgLabelData,
    ISvgLabelDropdownData,
    ITruesizeImage,
    IkeOfficeGenericDatum,
    Image,
    Mre,
    QcPhotoPosition,
    Span,
    TableSortField,
    WireSegment,
} from './Interfaces';
import { QualityControlActions } from './Reducer';

export const DEFAULT_IKE_OFFICE_IMAGE_HEIGHT = 3781;
export const DEFAULT_IKE_OFFICE_IMAGE_WIDTH = 2836;

/**
 * @param {GetSpansPole} corePole The raw Pole data from get_spans_v2 endpoint. The 'pole' attribute can be either 'selected' or 'adjacent'
 * @param {IQcPole} parsedPole The mapped object. Can be any of the SelectedPole or Fore/Back Poles.
 * @param {CancelToken} cancelToken Used to cancel REST requests.
 * @param {boolean} loadPoleTagImages Whether to fetch and parse the pole tag images. Only used by the center pole.
 */
export async function extractImagesFromPole(
    corePole: GetSpansPole,
    parsedPole: IQcPole,
    cancelToken: CancelToken,
    loadPoleTagImages = false
) {
    if (_isNil(corePole) || _isNil(parsedPole)) return;

    if (
        _isNil(corePole.images.true_size_images) ||
        corePole.images.true_size_images.length === 0
    )
        return;

    // map and set the truesize image data, fetching and setting the image with the most attachment points
    const truesizeImageData = await extractTruesizeImageData(
        corePole.images.true_size_images,
        cancelToken
    );

    parsedPole.truesizeImageUrls = truesizeImageData?.imageUrls ?? [];
    parsedPole.truesizeImageInfo = truesizeImageData?.imageInfo ?? [];

    if (loadPoleTagImages) {
        // map and set the pole tag image urls
        const poleTagImages = corePole.images.pole_tag_images.map((image) => {
            return {
                url: null,
                image_id: image.image_id,
                collection_field_name: image.collection_field_name,
            };
        });

        if (poleTagImages.length > 0) {
            const imgResp = await entityEndPoints.getEntityImage(
                poleTagImages[0].image_id,
                false,
                { cancelToken: cancelToken }
            );

            if (imgResp.isSuccess) {
                const img = blobToUrl(imgResp.data);
                poleTagImages[0].url = img;
                parsedPole.poletagLoadedImageUrl = poleTagImages[0].url;
            }
        }

        parsedPole.poletagImageUrls = poleTagImages;
    }

    // return the id of the image with the most attachment points, used to render the labels
    return truesizeImageData.attachmentImageId ?? null;
}

function getImageWithMostAttachmentPoints(imagesArray: Image[]): Image {
    if (imagesArray.length === 0) return null;

    const attachmentsArray = imagesArray.filter(
        (image) =>
            image.capture_type === 'truesize' &&
            image.attachment_point_set.length > 0
    );

    if (attachmentsArray.length === 0) return null;

    const sortedArray = _orderBy(
        attachmentsArray,
        (image) => image.attachment_point_set.length,
        'desc'
    );

    return sortedArray[0];
}

/**
 * Parse the spans attached to the central pole for the PlanView and ImageCarouselController components.
 */
export function mapAttachedSpans(spans: Span[]): ISelectedPoleAttachedSpan[] {
    return spans.map((s) => {
        return {
            span_id: s.span_id,
            bearing: s.span_value?.bearing,
            pole_2: s.pole_2,
            pole_1: s.pole_1,
            type: s.span_type,
        };
    });
}

function mapTruesizeImageInfo(image: Image, url?: string): ITruesizeImage {
    return {
        url: url ?? image.image_id,
        resolution: {
            width:
                image.image_resolution_width ?? DEFAULT_IKE_OFFICE_IMAGE_WIDTH,
            height:
                image.image_resolution_height ??
                DEFAULT_IKE_OFFICE_IMAGE_HEIGHT,
        },
        bearing: image.image_orientation_bearing,
        lat: image.lat,
        lng: image.lng,
    };
}

type ExtractedTruesizeImageData = {
    imageUrls: string[];
    imageInfo: ITruesizeImage[];
    attachmentImageId: string;
};

/**
 * @param {Image[]} images an array of images from a Pole or Span from get spans v2
 * @param {CancelToken} cancelToken used to cancel an outstanding REST request
 *
 * Takes an Image array, retrieves the image with the most attachment points, and returns an array of URLs and information used to render a truesize image
 */
async function extractTruesizeImageData(
    images: Image[],
    cancelToken: CancelToken
): Promise<ExtractedTruesizeImageData> {
    // find the truesize photo with the most attachments, and parse it
    const filteredArray = images.filter((s) => s.capture_type === 'truesize');

    if (filteredArray.length === 0) return null;

    // sort by the most attachments
    const sortedArray = filteredArray.sort(
        (a, b) => b.attachment_point_set.length - a.attachment_point_set.length
    );
    // take the image with the most attachments, leaving the remaining
    const attachmentsPhoto = sortedArray.shift();

    // hit the endpoint to get the first image
    const imgResp = await entityEndPoints.getEntityImage(
        attachmentsPhoto.image_id,
        false,
        { cancelToken: cancelToken }
    );

    const imageUrls = [];
    const imageInfo = [];

    if (imgResp.isSuccess) {
        // push the object URL to the image urls
        const img = blobToUrl(imgResp.data);
        imageUrls.push(img);

        // push the raw data to the image info array
        imageInfo.push(mapTruesizeImageInfo(attachmentsPhoto, img));
    }

    // put all other photos' ids into the arrays
    sortedArray.forEach((photo) => {
        imageUrls.push(photo.image_id);
        imageInfo.push(mapTruesizeImageInfo(photo));
    });

    return {
        imageInfo: imageInfo,
        imageUrls: imageUrls,
        attachmentImageId: attachmentsPhoto.image_id,
    };
}

/**
 * @param {Span} coreSpan The raw Span data from get_spans_v2 endpoint. This will always be relative to the center pole.
 * @param {IQcSpanEntity} parsedSpan The mapped object.
 * @param {CancelToken} cancelToken Used to cancel REST requests.
 */
export async function extractImagesFromSpan(
    coreSpan: Span,
    parsedSpan: IQcSpanEntity,
    cancelToken: CancelToken
) {
    if (_isNil(coreSpan) || _isNil(parsedSpan)) return;

    parsedSpan.span_id = coreSpan.span_id;

    const midSpanPhotoArray = coreSpan.span_mid_span_photo;

    if (_isNil(midSpanPhotoArray) || midSpanPhotoArray.length === 0) return;

    const truesizeImageData = await extractTruesizeImageData(
        midSpanPhotoArray,
        cancelToken
    );

    if (_isNil(truesizeImageData)) return;

    parsedSpan.truesizeImageUrls = truesizeImageData.imageUrls;
    parsedSpan.truesizeImageInfo = truesizeImageData.imageInfo;
}

/**
 * @param {Span} coreSpan The raw Span data from get_spans_v2 endpoint. This will always be relative to the center pole.
 * @param {IQcSpanEntity} parsedSpan The mapped object.
 */
export function extractLabelInfoFromSpan(
    coreSpan: Span,
    parsedSpan: IQcSpanEntity
) {
    if (_isNil(coreSpan) || _isNil(parsedSpan)) {
        return;
    }

    parsedSpan.title = coreSpan.span_environment ?? '';
    parsedSpan.bearing = coreSpan?.span_value?.bearing;
    parsedSpan.distance = convertMetersToFeet(coreSpan?.span_value?.distance);

    const midSpanPhotoArray = coreSpan.span_mid_span_photo;

    if (_isNil(midSpanPhotoArray) || midSpanPhotoArray.length === 0) return;

    const attachmentPhoto = getImageWithMostAttachmentPoints(midSpanPhotoArray);
    const attachmentPoints =
        attachmentPhoto?.attachment_point_set?.filter(
            (attachment) => attachment.point_type !== 'base'
        ) ?? [];

    attachmentPoints.forEach((attachmentPoint) => {
        const label: ISvgLabelData = {
            id: attachmentPoint.attachment_id,
            x: attachmentPoint.x_pixel,
            y: attachmentPoint.y_pixel,
            height: attachmentPoint.distance,
            name: attachmentPoint.collection_field_name,
        };

        if (parsedSpan.photoPosition === QcPhotoPosition.BACKSPAN) {
            parsedSpan.forwardLabels = [...parsedSpan.forwardLabels, label];
        } else if (parsedSpan.photoPosition === QcPhotoPosition.FORESPAN) {
            parsedSpan.backwardLabels = [...parsedSpan.backwardLabels, label];
        }
    });
}

/**
 * @param {GetSpansPole} corePole The forePole or backPole. Raw data from the get_spans endpoint.
 * @param {IQcPole} parsedPole The formatted pole that we use to render the QC view.
 * @param {Span} attachedSpan The raw data of the span attached between the selectedPole and the corePole. This is always relative to the selected pole.
 * @param {GetSpansPole} selectedPole The selected pole. Raw data from the get_spans endpoint
 */
export function extractLabelInfoFromOuterPole(
    corePole: GetSpansPole,
    parsedPole: IQcPole,
    attachedSpan: Span,
    selectedPole: GetSpansPole
) {
    if (_isNil(corePole) || _isNil(parsedPole) || _isNil(attachedSpan)) return;

    parsedPole.entityVersionId = corePole?.entity_version ?? '';
    parsedPole.title = corePole?.client_id[0] ?? '';

    // agl
    if (!_isNil(corePole.agl_attachment_height)) {
        const aglLabel = tryExtractAglLabel(corePole.agl_attachment_height);
        if (parsedPole.photoPosition === QcPhotoPosition.BACKPOLE) {
            parsedPole.forwardLabels.push(aglLabel);
        } else {
            parsedPole.backwardLabels.push(aglLabel);
        }
    }

    let selectedSpan = corePole.span.selected.find(
        (poleSpan) =>
            poleSpan.pole_1 === attachedSpan.pole_2 &&
            poleSpan.pole_2 === selectedPole.pole_id
    );

    if (selectedSpan == null) {
        // fallback
        selectedSpan = corePole.span.selected.find(
            (poleSpan) => poleSpan.pole_1 === attachedSpan.pole_2
        );
    }

    // wires
    const wireLabels = tryExtractWireSegmentLabels(selectedSpan?.wire_segments);
    if (parsedPole.photoPosition === QcPhotoPosition.BACKPOLE) {
        parsedPole.forwardLabels.push(...wireLabels);
    } else {
        parsedPole.backwardLabels.push(...wireLabels);
    }
}

/**
 * Given a pole and the spans that we intend to render, parse the data from the get_spans endpoint to render the pole's labels in the QC View.
 * @param {GetSpansPole} corePole The selected pole. Raw data from the get_spans endpoint.
 * @param {IQcPole} parsedPole The formatted pole that we use to render the QC view.
 * @param {Span} forespan The raw data of the forespan from the get_spans endpoint.
 * @param {Span} backspan The raw data of the backspan from the get_spans endpoint.
 * @param {string} selectedImageId The returned id from the extractImagesFromPole function.
 */
export function extractLabelInfoFromCenterPole(
    corePole: GetSpansPole,
    parsedPole: IQcPole,
    forespan?: Span,
    backspan?: Span,
    selectedImageId?: string
) {
    if (_isNil(corePole) || _isNil(parsedPole)) return;

    parsedPole.entityVersionId = corePole?.entity_version ?? '';
    parsedPole.title = corePole?.client_id[0] ?? '';

    // AGL labels
    if (!_isNil(corePole.agl_attachment_height)) {
        const aglLabel = tryExtractAglLabel(corePole.agl_attachment_height);
        if (!_isNil(aglLabel)) {
            parsedPole.backwardLabels.push(aglLabel);
        }
    }

    // Equipment labels
    corePole.equipments?.forEach((equipment) => {
        if (!_isNil(equipment.equipment_attachment_height)) {
            const equipmentLabels = tryExtractEquipmentLabels(equipment);
            if (!_isNil(equipmentLabels)) {
                parsedPole.backwardLabels.push(...equipmentLabels);
            }

            // add partial information about the equipment to the parsed pole for the PlanView component.
            const equipmentData = {
                id: equipment.equipment_id,
                bearing: equipment.equipment_orientation,
                type: equipment.equipment_size,
            };

            parsedPole.equipment.push(equipmentData);
        }
    });

    // Anchor and Anchor Guy labels
    corePole.anchors?.forEach((anchor, index) => {
        // add partial information about the anchor to the parsed pole for the PlanView component.
        parsedPole.anchors.push({
            id: anchor.anchor_id,
            bearing: anchor.anchor_bearing ?? null,
            type: anchor.anchor_type,
        });

        // a prefix for the name field of the labels for both the anchor and their guys, to show their relationship.
        const prefix = `A${index + 1}`;

        const anchorLabel = tryExtractAnchorLabel(anchor, prefix);

        if (!_isNil(anchorLabel)) {
            parsedPole.backwardNoHeightLabels.push(anchorLabel);
        }

        // add a label for each guy attached to the anchor.
        if (!_isNil(anchor.guys) && Array.isArray(anchor.guys)) {
            anchor.guys.forEach((guy) => {
                const anchorGuyLabels = tryExtractGuyLabel(
                    guy,
                    'anchor',
                    prefix
                );
                if (!_isNil(anchorGuyLabels)) {
                    parsedPole.backwardLabels.push(...anchorGuyLabels);
                }
            });
        }
    });

    // Wire segment labels
    const forespanWireLabels = tryExtractWireSegmentLabels(
        forespan?.wire_segments
    );
    if (!_isNil(forespanWireLabels)) {
        forespanWireLabels.forEach((label) => {
            if (!_isNil(label.height)) {
                parsedPole.forwardLabels.push(label);
            } else {
                parsedPole.forwardNoHeightLabels.push(label);
            }
        });
    }

    const backspanWireLabels = tryExtractWireSegmentLabels(
        backspan?.wire_segments
    );
    if (!_isNil(backspanWireLabels)) {
        backspanWireLabels.forEach((label) => {
            if (!_isNil(label.height)) {
                parsedPole.backwardLabels.push(label);
            } else {
                parsedPole.backwardNoHeightLabels.push(label);
            }
        });
    }

    //mres
    let noHeightMreIndex = 0;
    corePole.mres.forEach((mre, mreIndex) => {
        const { labels, noHeightLabels } = tryExtractMreLabels(
            mre,
            noHeightMreIndex,
            `Make Ready #${mreIndex + 1}`
        );

        if (noHeightLabels.length > 0) {
            parsedPole.forwardNoHeightLabels.push(...noHeightLabels);
            noHeightMreIndex += noHeightLabels.length;
        }

        parsedPole.forwardLabels.push(...labels);
    });

    //forespan span guys
    forespan?.span_guys?.forEach((guy) => {
        const fsGuyLabels = tryExtractGuyLabel(guy, 'span');
        parsedPole.forwardLabels.push(...fsGuyLabels);
    });

    //backspan span guys
    backspan?.span_guys?.forEach((guy) => {
        const bsGuyLabels = tryExtractGuyLabel(guy, 'span');
        parsedPole.backwardLabels.push(...bsGuyLabels);
    });

    // drop spans
    const dropSpans = corePole.span.selected.filter(
        (s) => s.span_type === 'Other' || s.span_type === 'Building'
    );
    dropSpans.forEach((dropspan) => {
        const dsLabel = tryExtractDropspanLabel(dropspan);
        if (!_isNil(dsLabel)) {
            if (!_isNil(dsLabel.height)) {
                parsedPole.forwardLabels.push(dsLabel);
            } else {
                parsedPole.forwardNoHeightLabels.push(dsLabel);
            }
        }
    });
    corePole.generic_data
        ?.filter((item) => {
            if (
                (item.type === 'height' &&
                    item.provider?.type === 'attachment_point') ||
                item.type === 'form'
            ) {
                return item;
            } else {
                // at the pole level simple fields are in the entity detail window, omit them
                return null;
            }
        })
        .forEach((datum) => {
            const parsedGenericDatumArray = tryParseCorePoleGenericData(datum);
            if (!_isNil(parsedGenericDatumArray)) {
                parsedGenericDatumArray.forEach((parsedGenericDatum) => {
                    if (!_isNil(parsedGenericDatum.height)) {
                        parsedPole.forwardLabels.push(parsedGenericDatum);
                    } else {
                        parsedPole.forwardNoHeightLabels.push(
                            parsedGenericDatum
                        );
                    }
                });
            }
        });

    // image attachments
    if (!_isNil(selectedImageId)) {
        const image = corePole.images.true_size_images.find(
            (image) => image.image_id === selectedImageId
        );

        // ignore attachments we have already added
        const existingIds = new Set(
            [...parsedPole.backwardLabels, ...parsedPole.forwardLabels].map(
                (obj) => obj.id
            )
        );

        const neededFields = ['base offset', 'proposed mid span'];
        let uniqueAttachments = [];
        image.attachment_point_set.forEach((attachment) => {
            if (
                neededFields.includes(attachment.label?.toLowerCase()) ||
                neededFields.includes(
                    attachment.collection_field_name?.toLowerCase()
                ) ||
                attachment.point_type === 'base'
            ) {
                uniqueAttachments.push(attachment);
            }
        });

        uniqueAttachments = [
            ...uniqueAttachments,
            ...image.attachment_point_set.filter(
                (attachment) =>
                    !existingIds.has(attachment.attachment_id) &&
                    !uniqueAttachments.includes(attachment) &&
                    (attachment.collection_field_name
                        ?.toLowerCase()
                        .includes('low power') ||
                        attachment.label?.toLowerCase().includes('attachment'))
            ),
        ];
        uniqueAttachments.forEach((attachment) => {
            const label: ISvgLabelData = {
                id: attachment.attachment_id,
                type: 'nonattached',
                point_type: attachment.point_type,
                x: attachment.x_pixel,
                y: attachment.y_pixel,
                height: attachment.distance,
                name:
                    attachment.label && attachment.label !== ''
                        ? attachment.label
                        : attachment.collection_field_name,
            };
            if (attachment.point_type === 'base') {
                //console.log(attachment)
                if (_isNil(attachment.offset)) {
                    parsedPole.backwardLabels.push(label);
                } else {
                    label.name = 'Base Offset';
                    const baseOffsetVectorCI = new Vector(label.x, label.y);
                    const truesize = image;
                    const distance = truesize?.image_distance;
                    const pitch = truesize?.image_orientation_pitch;

                    //console.log(JSON.stringify(truesize))
                    const px = convertHeightValueToPixels(
                        baseOffsetVectorCI,
                        distance,
                        pitch,
                        attachment.offset
                    );
                    label.height = attachment.offset;
                    const label2: ISvgLabelData = { ...label };
                    label2.name = 'Base';
                    label2.height = 0;
                    label2.offset = 0;
                    label2.y = px;
                    parsedPole.backwardLabels.push(label);
                    parsedPole.backwardLabels.push(label2);
                }
            } else if (!_isNil(attachment.distance)) {
                parsedPole.forwardLabels.push(label);
            } else {
                parsedPole.backwardNoHeightLabels.push(label);
            }
        });
    }
}

/**
 * Given a span that is attached to the selected pole, and the outer pole that it connects - extract the images and parse the data to render the labels.
 * @param {GetSpansPole} corePole An outer pole. Raw data from the get_spans endpoint.
 * @param {Span} coreSpan Raw data about a span from the get_spans endpoint. This is the data relative to the selected pole.
 * @param {IQcPole} parsedPole The formatted pole that we use to render the QC view.
 * @param {IQcSpanEntity} parsedSpan The formatted span that we use to render the QC view.
 */
export async function parseAttachedSpansAndPoles(
    corePole: GetSpansPole,
    coreSpan: Span,
    parsedPole: IQcPole,
    parsedSpan: IQcSpanEntity,
    cancelToken: CancelToken,
    selectedPole: GetSpansPole
) {
    // Download the image for the span and pole in parallel, and continue when both functions have resolved.
    await Promise.all([
        extractImagesFromSpan(coreSpan, parsedSpan, cancelToken),
        extractImagesFromPole(corePole, parsedPole, cancelToken),
    ]);
    extractLabelInfoFromSpan(coreSpan, parsedSpan);
    extractLabelInfoFromOuterPole(corePole, parsedPole, coreSpan, selectedPole);
}

type DropdownEdgeCase = {
    key: string;
    handler: (value: any) => string | number | React.ReactElement;
};

type MapDropdownOptions = {
    omittedAttributes?: string[];
    edgeCases?: DropdownEdgeCase[];
    labelParser?: (label: string) => string;
};

/**
 * Given an object - an attribute of a pole or span from the get_spans endpoint - map its keys and values so that they can be displayed as dropdown data.
 * @param {object} object An attribute of a GetSpansPole or Span, eg. AttachmentPoint, Equipment, etc.
 * @param {MapDropdownOptions} options Optional options to parse the entries.
 */
function mapDropdownData(
    object: object,
    options?: MapDropdownOptions
): ISvgLabelDropdownData[] {
    const omittedDropdownAttributes = options?.omittedAttributes ?? [];
    const edgeCases = options?.edgeCases ?? [];
    const labelParser =
        options?.labelParser ??
        function (label) {
            return label;
        };

    let output: ISvgLabelDropdownData[] = [];

    // for each key/value pair of the object
    Object.entries(object).forEach(([key, value]) => {
        // if we haven't chosen to omit it
        if (!omittedDropdownAttributes.includes(key) && !_isNil(value)) {
            const edgeCase = edgeCases.find((e) => e.key === key);

            // if it's an edge case that we are handling
            if (!_isNil(edgeCase)) {
                const edgeCaseValue = edgeCase.handler(value);
                output.push({ name: key, value: edgeCaseValue });
                // if it's generic_data from Ike Office
            } else if (key === 'generic_data') {
                if (Array.isArray(value)) {
                    value.forEach((genericDataValue: IkeOfficeGenericDatum) => {
                        output.push(
                            ...parseIkeOfficeGenericData(genericDataValue)
                        );
                    });
                } else if (typeof value === 'string') {
                    const second_level_obj = JSON.parse(value);
                    if (Array.isArray(second_level_obj)) {
                        second_level_obj.forEach(
                            (genericDataValue: IkeOfficeGenericDatum) => {
                                output.push(
                                    ...parseIkeOfficeGenericData(
                                        genericDataValue
                                    )
                                );
                            }
                        );
                    }
                } else {
                    console.error(
                        `generic_data has been incorrectly formatted ${value}, ${typeof value}`,
                        { object }
                    );
                }
                // if it is data we haven't handled correctly
            } else if (typeof value === 'object') {
                console.log('unhandled object passed to mapper:', {
                    key,
                    value,
                    object,
                });
                // else render the key/value pair
            } else {
                output.push({
                    name: key,
                    value: value,
                });
            }
        }
    });

    output = output.map((o) => {
        return {
            ...o,
            name: labelParser(o.name),
        };
    });

    return output;
}

/**
 * Add an attachment point object to a given array if it is viable
 */
function tryAddToAttachmentPointArray(
    object: object,
    field: string,
    array: AttachmentPoint[]
) {
    if (
        Object.hasOwn(object, field) &&
        !_isNil(object[field]) &&
        typeof object[field] === 'object'
    ) {
        array.push(object[field]);
    }
}

/**
 * Parse an Anchor object from the get_spans endpoint
 */
function tryExtractAnchorLabel(anchor: Anchor, prefix: string): ISvgLabelData {
    try {
        const omittedAttributes = [
            'anchor_id',
            'anchor_lead_length',
            'pole',
            'guys',
        ];

        const labelParser = (label: string): string => {
            let output = label;
            output = output.replace('anchor_', '');
            output = splitCamelCase(output, true);
            return output;
        };

        const edgeCases: DropdownEdgeCase[] = [
            {
                key: 'anchor_bearing',
                handler: (value: number) => convertRadiansToDegrees(value),
            },
            {
                key: 'anchor_distance',
                handler: (value: number) => {
                    return convertMetersToFeet(value);
                },
            },
        ];

        const dropdownAttributes = mapDropdownData(anchor, {
            labelParser: labelParser,
            omittedAttributes: omittedAttributes,
            edgeCases: edgeCases,
        });

        return {
            id: anchor.anchor_id,
            type: 'anchor',
            height: null,
            name: `${prefix} ${anchor.anchor_owner}`,
            dropdownData: dropdownAttributes,
        };
    } catch (e) {
        throwError(e, 'anchor');
        return null;
    }
}

/**
 * Return labels for each attachment_point for either a Span or Anchor Guy.
 * @param {Guy} guy
 * @param {'span' | 'anchor'} type
 * @param {string} prefix only used for anchor guys, used to show which guys are related to an anchor
 */
function tryExtractGuyLabel(
    guy: Guy,
    type: 'span' | 'anchor',
    prefix?: string
): ISvgLabelData[] {
    try {
        const guyType = type === 'span' ? 'spanGuy' : 'guy';
        const guyName = prefix ? `${prefix} ${guy.guy_size}` : guy.guy_size;

        const omittedAttributes = [
            'anchor',
            'guy_id',
            'span',
            'guy_attachment_height',
            'guy_attachment_height_at_wep',
            'guy_midspan_attachment_height',
        ];

        const labelParser = (label: string): string => {
            let output = label;
            output = output.replace('guy_', '');
            output = splitCamelCase(output, true);
            return output;
        };

        const guyAttachmentPoints = [];

        tryAddToAttachmentPointArray(
            guy,
            'guy_attachment_height',
            guyAttachmentPoints
        );
        tryAddToAttachmentPointArray(
            guy,
            'guy_attachment_height_at_wep',
            guyAttachmentPoints
        );
        tryAddToAttachmentPointArray(
            guy,
            'guy_midspan_attachment_height',
            guyAttachmentPoints
        );

        const dropdownData = mapDropdownData(guy, {
            omittedAttributes,
            labelParser,
        });

        return guyAttachmentPoints.map((attachmentPoint: AttachmentPoint) => {
            return {
                id: guy.guy_id,
                type: guyType,
                point_type: attachmentPoint?.point_type ?? null,
                x: attachmentPoint?.x_pixel ?? null,
                y: attachmentPoint?.y_pixel ?? null,
                height: attachmentPoint?.distance ?? 0,
                name: guyName,
                dropdownData: dropdownData,
            };
        });
    } catch (e) {
        throwError(e, type + ' guy');
        return [];
    }
}

/**
 * Try to render the Tip attachment point.
 */
function tryExtractAglLabel(
    aglAttachmentHeight: AttachmentPoint
): ISvgLabelData {
    const requiredAttributes = ['distance', 'x_pixel', 'y_pixel'];
    const shouldContinue = requiredAttributes.some((attribute) => {
        return (
            Object.hasOwn(aglAttachmentHeight, attribute) &&
            !_isNil(aglAttachmentHeight[attribute])
        );
    });

    if (!shouldContinue) return null;

    try {
        const label: ISvgLabelData = {
            id: aglAttachmentHeight.attachment_id ?? '',
            type: 'agl',
            point_type: aglAttachmentHeight.point_type ?? '',
            x: aglAttachmentHeight.x_pixel ?? 0,
            y: aglAttachmentHeight.y_pixel ?? 0,
            height: aglAttachmentHeight.distance ?? 0,
            name: 'Tip',
        };
        return label;
    } catch (e) {
        throwError(e, 'agl attachment');
        return null;
    }
}

type WireAttachmentAttributes = {
    point_type: string;
    x: number;
    y: number;
    height: number;
    name: string;
};
function tryExtractWireSegmentLabels(
    wireSegments: WireSegment[]
): ISvgLabelData[] {
    if (_isNil(wireSegments)) return [];

    const omittedDropdownAttributes = [
        'attachment_point_2',
        'parent_equipment_id',
        'equipment_bottom_height',
        'wire',
        'wire_segment_id',
        'span',
    ];

    const edgeCases: DropdownEdgeCase[] = [
        {
            key: 'equipment_orientation',
            handler: (value: number) => {
                return roundToFourSigFigs(convertRadiansToDegrees(value));
            },
        },
    ];

    const labelParser = (label: string): string => {
        let output = label;
        output = splitCamelCase(output, true);
        return output;
    };

    const dropdownOptions = {
        omittedAttributes: omittedDropdownAttributes,
        edgeCases: edgeCases,
        labelParser: labelParser,
    };

    function parseAttachmentAttributes(
        attachment: AttachmentPoint,
        name: string
    ): WireAttachmentAttributes {
        return {
            point_type: attachment.point_type ?? null,
            x: attachment.x_pixel ?? null,
            y: attachment.y_pixel ?? null,
            height: attachment.distance ?? null,
            name: name ?? '',
        };
    }

    function concatPhaseName(phase: AttachmentPoint): string {
        const fieldIds = _groupBy(phase.collection_field_ids);
        const mapped = Object.entries(fieldIds).map(([key, value]) => {
            return {
                key: key,
                count: (value as string[]).length,
            };
        });
        const ordered = _orderBy(mapped, 'count', 'desc');
        return ordered.map((obj) => `${obj.count}x${obj.key}`).join(' ');
    }

    const output = [];
    wireSegments.forEach((segment) => {
        try {
            const segmentDropdownOptions: MapDropdownOptions =
                _cloneDeep(dropdownOptions);

            let attachmentAttributes: WireAttachmentAttributes;
            if (!_isNil(segment.attachment_point_1)) {
                // if we have an attachment point 1, ignore the phases
                segmentDropdownOptions.omittedAttributes.push(
                    ...[
                        'attachment_point_phase_a',
                        'attachment_point_phase_b',
                        'attachment_point_phase_c',
                    ]
                );

                segmentDropdownOptions.edgeCases.push({
                    key: 'attachment_point_1',
                    handler: (value: AttachmentPoint) =>
                        getAttachmentPointHeightInFeet(value),
                });

                attachmentAttributes = parseAttachmentAttributes(
                    segment.attachment_point_1,
                    segment.size
                );
            } else {
                // ignore the attachment point
                segmentDropdownOptions.omittedAttributes.push(
                    'attachment_point_1'
                );

                // parse one phase from a -> b -> c
                if (!_isNil(segment.attachment_point_phase_a)) {
                    segmentDropdownOptions.omittedAttributes.push(
                        ...[
                            'attachment_point_phase_b',
                            'attachment_point_phase_c',
                        ]
                    );

                    segmentDropdownOptions.edgeCases.push({
                        key: 'attachment_point_phase_a',
                        handler: (value: AttachmentPoint) =>
                            getAttachmentPointHeightInFeet(value),
                    });

                    attachmentAttributes = parseAttachmentAttributes(
                        segment.attachment_point_phase_a,
                        concatPhaseName(segment.attachment_point_phase_a)
                    );
                } else if (!_isNil(segment.attachment_point_phase_b)) {
                    segmentDropdownOptions.omittedAttributes.push(
                        ...[
                            'attachment_point_phase_a',
                            'attachment_point_phase_c',
                        ]
                    );

                    segmentDropdownOptions.edgeCases.push({
                        key: 'attachment_point_phase_b',
                        handler: (value: AttachmentPoint) =>
                            getAttachmentPointHeightInFeet(value),
                    });

                    attachmentAttributes = parseAttachmentAttributes(
                        segment.attachment_point_phase_b,
                        concatPhaseName(segment.attachment_point_phase_b)
                    );
                } else if (!_isNil(segment.attachment_point_phase_c)) {
                    segmentDropdownOptions.omittedAttributes.push(
                        ...[
                            'attachment_point_phase_a',
                            'attachment_point_phase_b',
                        ]
                    );

                    segmentDropdownOptions.edgeCases.push({
                        key: 'attachment_point_phase_c',
                        handler: (value: AttachmentPoint) =>
                            getAttachmentPointHeightInFeet(value),
                    });

                    attachmentAttributes = parseAttachmentAttributes(
                        segment.attachment_point_phase_c,
                        concatPhaseName(segment.attachment_point_phase_c)
                    );
                }
            }

            if (!_isNil(attachmentAttributes)) {
                output.push({
                    id: segment.wire_segment_id ?? '',
                    type: 'wire',
                    dropdownData: mapDropdownData(
                        segment,
                        segmentDropdownOptions
                    ),
                    ...attachmentAttributes,
                });
            } else {
                segmentDropdownOptions.omittedAttributes.push(
                    ...[
                        'attachment_point_phase_a',
                        'attachment_point_phase_b',
                        'attachment_point_phase_c',
                    ]
                );
                output.push({
                    id: segment.wire_segment_id ?? '',
                    type: 'wire',
                    dropdownData: mapDropdownData(
                        segment,
                        segmentDropdownOptions
                    ),
                    name: segment.size,
                    x: null,
                    y: null,
                    height: null,
                    point_type: null,
                });
            }
        } catch (e) {
            throwError(e, 'wire');
        }
    });

    return output;
}

function getAttachmentPointHeightInFeet(value: AttachmentPoint): string {
    if (
        _isNil(value) ||
        typeof value !== 'object' ||
        !Object.hasOwn(value, 'distance')
    ) {
        return null;
    }
    return convertMetersToFeet(value.distance);
}

/**
 * Return labels for each attachment_point for a piece of Equipment.
 */
function tryExtractEquipmentLabels(equipment: Equipment): ISvgLabelData[] {
    const omittedDropdownAttributes = [
        'equipment_id',
        'pole',
        'parent_equipment_id',
    ];

    const edgeCases: DropdownEdgeCase[] = [
        {
            key: 'equipment_attachment_height',
            handler: (value: AttachmentPoint) =>
                getAttachmentPointHeightInFeet(value),
        },
        {
            key: 'equipment_bottom_height',
            handler: (value: AttachmentPoint) =>
                getAttachmentPointHeightInFeet(value),
        },
        {
            key: 'equipment_orientation',
            handler: (value: number) => {
                return roundToFourSigFigs(convertRadiansToDegrees(value));
            },
        },
    ];

    const attachmentPoints = [];
    tryAddToAttachmentPointArray(
        equipment,
        'equipment_attachment_height',
        attachmentPoints
    );
    tryAddToAttachmentPointArray(
        equipment,
        'equipment_bottom_height',
        attachmentPoints
    );

    const labelParser = (label: string): string => {
        let output = label;
        output = output.replace('equipment_', '');
        output = splitCamelCase(output, true);
        return output;
    };

    const dropdownData = mapDropdownData(equipment, {
        omittedAttributes: omittedDropdownAttributes,
        edgeCases: edgeCases,
        labelParser: labelParser,
    });

    try {
        return attachmentPoints.map((attachmentPoint) => {
            return {
                id: equipment.equipment_attachment_height.attachment_id,
                type: 'equipment',
                point_type: attachmentPoint.point_type,
                x: attachmentPoint.x_pixel,
                y: attachmentPoint.y_pixel,
                height: attachmentPoint.distance,
                name: `${equipment.equipment_type} > ${equipment.equipment_size}`,
                dropdownData: dropdownData,
            };
        });
    } catch (e) {
        throwError(e, 'equipment');
        return [];
    }
}

function tryExtractDropspanLabel(dropspan: Span): ISvgLabelData {
    try {
        const wireSegment = dropspan.wire_segments[0] ?? null;

        let spanDropdownData: ISvgLabelDropdownData[] = [
            {
                name: 'pitch',
                value: dropspan.span_value?.pitch ?? null,
            },
            {
                name: 'bearing',
                value:
                    convertRadiansToDegrees(dropspan.span_value?.bearing) ??
                    null,
            },
            {
                name: 'distance',
                value:
                    convertMetersToFeet(dropspan.span_value?.distance) ?? null,
            },
        ];

        if (!_isNil(wireSegment)) {
            const wireLabel = tryExtractWireSegmentLabels([wireSegment]);
            spanDropdownData = [
                ...spanDropdownData,
                ...wireLabel[0].dropdownData,
            ];
        }

        return {
            id: dropspan.span_id,
            name: wireSegment?.size ?? 'Dropspan',
            type: 'dropspan',
            point_type: wireSegment?.attachment_point_1?.point_type ?? null,
            x: wireSegment?.attachment_point_1?.x_pixel ?? null,
            y: wireSegment?.attachment_point_1?.y_pixel ?? null,
            height: wireSegment?.attachment_point_1?.distance ?? null,
            dropdownData: spanDropdownData,
        };
    } catch (e) {
        throwError(e, 'dropspan');
        return null;
    }
}

type ExtractedFormData = {
    attachmentPoints: FormGenericDatumValue[];
    dropdownData: IkeOfficeGenericDatum[];
};

function tryParseCorePoleGenericData(
    genericDatum: IkeOfficeGenericDatum
): ISvgLabelData[] {
    function createGenericDatumLabel(
        datum: IkeOfficeGenericDatum,
        dropdownData: ISvgLabelDropdownData[]
    ): ISvgLabelData {
        const hasHeight =
            Object.hasOwn(datum, 'provider') &&
            datum.provider.type === 'attachment_point';

        return {
            id: datum.field,
            type: 'generic',
            height: hasHeight ? datum.provider.attachment_point.distance : null,
            name: datum.name,
            dropdownData: dropdownData,
            x: hasHeight ? datum.provider.attachment_point.x_pixel : null,
            y: hasHeight ? datum.provider.attachment_point.y_pixel : null,
            point_type: hasHeight
                ? datum.provider.attachment_point.point_type
                : null,
        };
    }

    function extractFormData(
        genericDatum: IkeOfficeGenericDatum
    ): ExtractedFormData {
        const dropdownData = [];
        const attachmentPoints = [];
        if (Array.isArray(genericDatum.value)) {
            genericDatum.value.forEach((formValue: FormGenericDatumValue) => {
                if (hasAttachmentPoint(formValue.fields)) {
                    attachmentPoints.push(formValue);
                } else {
                    dropdownData.push(...formValue.fields);
                }
            });
        }
        return {
            dropdownData: dropdownData,
            attachmentPoints: attachmentPoints,
        };
    }

    function hasAttachmentPoint(fields: IkeOfficeGenericDatum[]): boolean {
        return fields.some(
            (field) =>
                Object.hasOwn(field, 'provider') &&
                field.provider.type === 'attachment_point'
        );
    }

    try {
        const output = [];

        // if it's a form, map its values as dropdown children
        if (
            Object.hasOwn(genericDatum, 'type') &&
            genericDatum.type === 'form'
        ) {
            // loop through all of the fields for each value.
            // if any of the field is an attachment point, render the value as a separate label.

            const extractedFormData = extractFormData(genericDatum);

            extractedFormData.attachmentPoints.forEach((attachmentPoint) => {
                const attachmentPointField = attachmentPoint.fields.find(
                    (f) => f.provider && f.provider.type === 'attachment_point'
                );
                const name = `${genericDatum.name} > ${attachmentPointField.provider.attachment_point?.collection_field_name}`;

                const dropdownData: ISvgLabelDropdownData[] = [];
                attachmentPoint.fields.forEach((datum) => {
                    dropdownData.push(...parseIkeOfficeGenericData(datum));
                });

                output.push({
                    id: attachmentPointField.field,
                    type: 'generic',
                    height: attachmentPointField.provider.attachment_point
                        .distance,
                    name: name,
                    dropdownData: dropdownData,
                    x: attachmentPointField.provider.attachment_point.x_pixel,
                    y: attachmentPointField.provider.attachment_point.y_pixel,
                    point_type:
                        attachmentPointField.provider.attachment_point
                            .point_type,
                });
            });

            if (extractedFormData.dropdownData.length > 0) {
                const dropdownData: ISvgLabelDropdownData[] = [];
                extractedFormData.dropdownData.forEach((datum) => {
                    dropdownData.push(...parseIkeOfficeGenericData(datum));
                });

                output.push(
                    createGenericDatumLabel(genericDatum, dropdownData)
                );
            }
        } else {
            output.push(
                createGenericDatumLabel(
                    genericDatum,
                    parseIkeOfficeGenericData(genericDatum)
                )
            );
        }
        return output;
    } catch (e) {
        throwError(e, 'generic data');
        return [];
    }
}

function tryExtractMreLabels(
    mre: Mre,
    noHeightIndex: number,
    defaultName: string
): { noHeightLabels: ISvgLabelData[]; labels: ISvgLabelData[] } {
    try {
        const noHeightLabels = [],
            labels = [];

        // every piece of generic data that is related to height is its own label
        const subforms = mre.generic_data.filter(
            (datum) => datum.type === 'form'
        );

        const subform_attachments_values = [];
        subforms.forEach((form) => {
            const subforms_value = form.value;
            subforms_value.forEach((subform_item) => {
                subform_item.fields.forEach((inner_item) => {
                    if (
                        inner_item.type === 'height' &&
                        !_isNil(inner_item.provider) &&
                        inner_item.provider.type === 'photomeasure'
                    ) {
                        console.error('error photomeasure ');
                    } else if (
                        inner_item.type === 'height' &&
                        !_isNil(inner_item.provider) &&
                        inner_item.provider.type === 'attachment_point'
                    ) {
                        const processed_item = { ...inner_item };
                        subform_attachments_values.push(processed_item);
                    }
                });
            });
        });

        let heightAttributes = mre.generic_data.filter(
            (datum) =>
                datum.type === 'height' &&
                !_isNil(datum.provider) &&
                datum.provider.type !== 'manual' &&
                datum.provider.type !== 'photomeasure'
        );
        heightAttributes = [...subform_attachments_values, ...heightAttributes];
        const nameDatum = mre.generic_data.find(
            (datum) => datum.name === 'Attachment Size'
        );
        const notesDatum = mre.generic_data.find(
            (datum) => datum.name === 'Make Ready Notes'
        );

        const dropdownAttributes = mre.generic_data.filter(
            (datum) => datum.type !== 'height'
        );
        const dropdownData = mapDropdownData({
            generic_data: dropdownAttributes,
        });

        if (heightAttributes.length > 0) {
            heightAttributes.forEach((heightDatum) => {
                const label: ISvgLabelData = {
                    id: mre.mre_id,
                    type: 'mre',
                    point_type:
                        heightDatum.provider.attachment_point?.point_type ??
                        null,
                    x: heightDatum.provider.attachment_point?.x_pixel ?? null,
                    y: heightDatum.provider.attachment_point?.y_pixel ?? null,
                    height: heightDatum.value,
                    name: '',
                    dropdownData: dropdownData,
                };

                if (heightDatum.provider.type === 'attachment_point') {
                    label.name =
                        nameDatum?.value && nameDatum.value !== ''
                            ? nameDatum.value
                            : defaultName;
                    labels.push(label);
                } else {
                    label.name =
                        notesDatum?.value && notesDatum.value !== ''
                            ? notesDatum.value
                            : defaultName;
                    label.index = noHeightIndex;
                    noHeightLabels.push(label);
                }
            });
        } else {
            noHeightLabels.push({
                id: mre.mre_id,
                type: 'mre',
                name:
                    notesDatum?.value && notesDatum.value !== ''
                        ? notesDatum.value
                        : defaultName,
                dropdownData: dropdownData,
            });
        }

        return { labels, noHeightLabels };
    } catch (error) {
        throwError(error, 'mre');
        return { noHeightLabels: [], labels: [] };
    }
}

function throwError(e, type: string) {
    console.log(e);
    error(`Error trying to render ${type} label. ${e}`);
}

export const convertHeightValueToPixels = (
    base: Vector,
    distance,
    pitch,
    offset
) => {
    // generic okay calibration ike4
    const calibration: GeometryCalibration = {
        center: { x: 27, y: 4 },
        distortion: [
            -0.08410691869274017, -0.29923999272507573, 1.4882187605973525,
        ],
        focal: 3326.363327833272,
        rotation: {
            x: -0.00377632313491957,
            y: -0.001790705872420466,
            z: 0.00398834602930168,
        },
        translation: {
            x: 0.0017088521575091716,
            y: 0.7247419363081484,
            z: -3.651,
        },
    };

    // todo bring in calibration for device
    if (calibration == null) {
        throw new Error(`Capture Unable to find calibration`);
    }
    const geometry: Geometry3D = PitchGeometryFactory.create({
        pitch: pitch,
        distance: distance,
        resolution: {
            width: DEFAULT_IKE_OFFICE_IMAGE_WIDTH,
            height: DEFAULT_IKE_OFFICE_IMAGE_HEIGHT,
        },
        calibration: calibration,
    });

    function calculateM2Px(
        baseMarkerXY: Vector,
        offset: number,
        geometry: Geometry3D
    ) {
        const moveVector = new Vector(0, offset);
        const moveDistance = geometry.movePoint(baseMarkerXY, moveVector);
        return moveDistance;
    }

    const value_in_pix = calculateM2Px(base, offset, geometry);
    return value_in_pix.y;
};

export function getSortedEntities(
    entities: IAnalysisEntity[],
    sortOrder: SortOrder,
    field: TableSortField
): IAnalysisEntity[] {
    const sortOrderStr = sortOrder === SortOrder.ASC ? 'asc' : 'desc';

    switch (field) {
        case TableSortField.CLIENT_ID:
            return _orderBy(entities, ['client_id'], [sortOrderStr]);
        case TableSortField.QC_STATE:
            return _orderBy(entities, ['qcState'], [sortOrderStr]);

        default:
            return entities;
    }
}

export enum GlobalAxiosCancelTokenKey {
    GET_SVG_VIEWER_DATA = 'getSvgViewerData',
    UPDATE_BACK_POLE_AND_SPAN = 'updateBackPoleAndSpan',
    UPDATE_FORE_POLE_AND_SPAN = 'updateForePoleAndSpan',
    GET_GEOSERVER_DATA = 'getGeoserverData',
}

/**
 * create a key for a global variable for an Axios Cancel Token
 * intended to be used on a per function basis - so that a call that
 * uses the same logic will cancel outstanding requests.
 */

const getTokenGlobalKey = (key: GlobalAxiosCancelTokenKey): string => {
    return `${GlobalKeys.CancelTokens}${key}`;
};

/**
 * cancel all outstanding requests for a particular function
 */
export const cancelOutstandingQcRequests = (key: GlobalAxiosCancelTokenKey) => {
    const tokenKey = getTokenGlobalKey(key);

    if (hasGlobalValue(tokenKey)) {
        const tokens: CancelTokenSource[] = getGlobalVariable(tokenKey);
        tokens.forEach((token) => {
            token.cancel();
        });
        addGlobalVariable(tokenKey, []);
    }
};

/**
 * add an Axios cancellation token source for a particular function as a
 * global variable
 */
export const addQcCancelToken = (key: GlobalAxiosCancelTokenKey) => {
    const tokenKey = getTokenGlobalKey(key);
    const tokensArray: CancelTokenSource[] = hasGlobalValue(tokenKey)
        ? getGlobalVariable(tokenKey)
        : [];

    const newSource = axios.CancelToken.source();
    tokensArray.push(newSource);
    addGlobalVariable(tokenKey, tokensArray);
    return newSource.token;
};

/**
 * clear all cancel token sources, intended to be used when a QC run is started/finished to dump it from memory
 */
export const clearAllQcCancelTokens = () => {
    Object.values(GlobalAxiosCancelTokenKey).forEach((key) => {
        clearGlobalVariable(getTokenGlobalKey(key));
    });
};

/**
 * stop outstanding requests when the QC run exits
 */
export const callAllQcCancelTokens = () => {
    Object.values(GlobalAxiosCancelTokenKey).forEach((key) => {
        cancelOutstandingQcRequests(key);
    });
};

export function cloneReasonAndAddComment(
    reason: IReworkReason,
    comment: string
): IReworkReason {
    const clonedReason = _cloneDeep(reason);
    clonedReason.comment = comment;
    return clonedReason;
}

export const findAndUpdateUrl = (
    photoPositionIndex: number,
    imageIndex: number,
    imageUrl: string
) => {
    switch (photoPositionIndex) {
        case 0: {
            const currentBackPole = store.getState().qualityControl.backPole;
            const newTruesizeUrlArray = currentBackPole.truesizeImageUrls.map(
                (url, index) => {
                    const value = index === imageIndex ? imageUrl : url;
                    return value;
                }
            );
            store.dispatch(
                QualityControlActions.updateBackPoleData({
                    ...currentBackPole,
                    truesizeImageUrls: newTruesizeUrlArray,
                })
            );
            break;
        }
        case 1: {
            const currentBackSpan = store.getState().qualityControl.backSpan;
            const newTruesizeUrlArray = currentBackSpan.truesizeImageUrls.map(
                (url, index) => {
                    const value = index === imageIndex ? imageUrl : url;
                    return value;
                }
            );
            store.dispatch(
                QualityControlActions.updateBackSpanData({
                    ...currentBackSpan,
                    truesizeImageUrls: newTruesizeUrlArray,
                })
            );
            break;
        }
        case 2: {
            const currentCentrePole =
                store.getState().qualityControl.centrePole;
            const newTruesizeUrlArray = currentCentrePole.truesizeImageUrls.map(
                (url, index) => {
                    const value = index === imageIndex ? imageUrl : url;
                    return value;
                }
            );
            store.dispatch(
                QualityControlActions.updateCentrePoleData({
                    ...currentCentrePole,
                    truesizeImageUrls: newTruesizeUrlArray,
                })
            );
            break;
        }
        case 3: {
            const currentForeSpan = store.getState().qualityControl.foreSpan;
            const newTruesizeUrlArray = currentForeSpan.truesizeImageUrls.map(
                (url, index) => {
                    const value = index === imageIndex ? imageUrl : url;
                    return value;
                }
            );
            store.dispatch(
                QualityControlActions.updateForeSpanData({
                    ...currentForeSpan,
                    truesizeImageUrls: newTruesizeUrlArray,
                })
            );
            break;
        }
        case 4: {
            const currentForePole = store.getState().qualityControl.forePole;
            const newTruesizeUrlArray = currentForePole.truesizeImageUrls.map(
                (url, index) => {
                    const value = index === imageIndex ? imageUrl : url;
                    return value;
                }
            );
            store.dispatch(
                QualityControlActions.updateForePoleData({
                    ...currentForePole,
                    truesizeImageUrls: newTruesizeUrlArray,
                })
            );
            break;
        }
    }
};

/**
 * Given a span that is attached to the selected pole, find the pole that is connected to.
 * @param {Span} span A Span that is connected to the selected pole. Raw data from the get_spans endpoint.
 * @param {GetSpansPole[]} poles The 'other' poles from the get_spans endpoint - ie. all but the selected pole.
 */
export function getAttachedPoleBySpan(
    span: Span,
    poles: GetSpansPole[]
): GetSpansPole {
    return poles.find((p) => p.pole_id === span.pole_2);
}

export function shuffleAnalysisEntities(
    entities: IAnalysisEntity[]
): IAnalysisEntity[] {
    const shuffledEntities = [...entities];
    for (let i = shuffledEntities.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffledEntities[i], shuffledEntities[j]] = [
            shuffledEntities[j],
            shuffledEntities[i],
        ];
    }
    return shuffledEntities;
}

function parseIkeOfficeGenericData(
    datum: IkeOfficeGenericDatum,
    prefix: string = null
): ISvgLabelDropdownData[] {
    function throwUnhandled(message: string) {
        throwError(message, datum.type);
        console.log(datum);
        return [];
    }
    let datumName: string;
    if (prefix != null) {
        datumName = `${prefix} ${datum.name}`;
    } else {
        datumName = datum.name;
    }

    try {
        let output = [];
        if (!datum.type) {
            console.log(datum);
        }
        switch (datum.type) {
            case 'attachment_height':
            case 'height':
                output.push({
                    name: datumName,
                    value: convertMetersToFeet(datum.value),
                });
                break;
            case 'yesno':
                output.push({
                    name: datumName,
                    value: datum.value ? 'Yes' : 'No',
                });
                break;
            case 'text':
            case 'date':
            case 'number':
                output.push({
                    name: datumName,
                    value: datum.value,
                });
                break;
            case 'selectlist':
                output.push({
                    name: datumName,
                    value: datum.value.title,
                });
                break;
            case 'nestedlist':
                output.push({
                    name: datumName,
                    value: datum.value.map((v) => v.value).join('>'),
                });
                break;
            case 'vector':
                if (
                    Object.hasOwn(datum, 'provider') &&
                    Object.hasOwn(datum.provider, 'type')
                ) {
                    output.push({
                        name: 'provider type',
                        value: datum.provider.type,
                    });
                }

                Object.entries(datum.value).forEach(([key, value]) => {
                    output.push({
                        name: key,
                        value: value,
                    });
                });
                break;
            case 'location':
                if (Object.hasOwn(datum, 'provider')) {
                    Object.entries(datum.provider).forEach(([key, value]) => {
                        output.push({
                            name: key,
                            value: value,
                        });
                    });
                }
                Object.entries(datum.value).forEach(([key, value]) => {
                    output.push({
                        name: key,
                        value: value,
                    });
                });
                break;
            case 'form':
                Object.entries(datum.value).forEach(([, value], idx) => {
                    const obj = value['fields'];
                    obj.forEach((genericDataValue: IkeOfficeGenericDatum) => {
                        output.push(
                            ...parseIkeOfficeGenericData(
                                genericDataValue,
                                `${datum.name} ${idx}`
                            )
                        );
                    });
                });
                break;
            default:
                return throwUnhandled(`unhandled type: ${datum.type}`);
        }

        output = output.map((o) => {
            return {
                ...o,
                name: splitCamelCase(o.name),
            };
        });
        return output;
    } catch (e) {
        throwUnhandled(e);
    }
}
